/* eslint-disable @typescript-eslint/no-explicit-any */

import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Button from "../buttons/Button";
import { errorAlert, makeApiRequest, successAlert } from "../../util";
import AuthContext from "../../store/authContext";
import { ADD_LOCATIONS_FORM_FIELDS } from "../../constants";
import Form from "../../ui/Form/Form";
import TextField from "../inputs/TextField";
import { ILocationData } from "../../types";
import DeleteModal, { DeleteModalIcons } from "../modal/DeleteModal";
import {
  CREATE_LOCATION_TRANSLATIONS,
  FORM_VALIDATION_TRANSLATIONS,
} from "../../translations/util";

const set = new Set();

const isInValidNumberForField = (val?: number, min?: number, max?: number) => {
  if (val || val === 0) {
    return val < (min || 0) || val > (max || 1048575);
  }

  return true;
};

const isFormInvalid = ({ data }: { data: any }) => {
  if (!data) return true;

  const isConnectionFormInvalid = ADD_LOCATIONS_FORM_FIELDS.map((rows: any) => {
    const res = rows.fields.map((field: any) => {
      switch (field.type) {
        case "TEXT":
          return field.isInputInvalid?.(data[field.name]);
        case "NUMBER":
          return isInValidNumberForField(
            data[field.name],
            field.min,
            field.max
          );
        default:
          return false;
      }
    });

    return res.reduce((acc: any, el: any) => acc || el);
  }).reduce((acc: any, el: any) => acc || el);

  return isConnectionFormInvalid;
};

const NewLocationForm = () => {
  const { token } = useContext(AuthContext);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  useEffect(() => {
    set.clear();
    return () => {
      set.clear();
    };
  }, []);

  const [connectionData, setConnectionData] = useState<ILocationData>({
    "IWF ID": undefined,
    "IWF Location Name": undefined,
    "IWF Domain Name": undefined,
  });

  const createLocation = async () => {
    setLoading(true);
    try {
      await makeApiRequest<{ Ref: string }>({
        path: `/api/v1/app-config/locations`,
        method: "POST",
        body: JSON.stringify({
          "IWF-ID": connectionData["IWF ID"],
          "IWF-LOCATION-NAME": connectionData["IWF Location Name"],
          "IWF-DOMAIN-NAME": connectionData["IWF Domain Name"],
        }),
        token,
      });
      successAlert(CREATE_LOCATION_TRANSLATIONS.successMsg);
      navigate("/configurationManagement/locations");
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      errorAlert(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="data-form">
        <Form>
          <div className="data-tabcontent data-card">
            {ADD_LOCATIONS_FORM_FIELDS.map(
              (rows: {
                cols: number;
                fields: {
                  name: keyof ILocationData;
                  tooltipText?: string;
                  label: string;
                  type: string;
                  invalidText?: string | null;
                  isInputInvalid?: (val?: string) => boolean;
                  min?: number;
                  max?: number;
                }[];
              }) => {
                return (
                  <div
                    className={`grid grid-cols-${rows.cols} gap-4 form-control`}
                  >
                    {rows.fields.map((field) => {
                      return (
                        <TextField
                          onChange={(val) => {
                            set.add(field.name);
                            setConnectionData({
                              ...connectionData,
                              [field.name]: val,
                            });
                          }}
                          placeholder={field.label}
                          value={connectionData[field.name] as string}
                          invalid={
                            set.has(field.name) &&
                            field.isInputInvalid?.(
                              connectionData[field.name] as string
                            )
                          }
                          invalidText={
                            field.invalidText ||
                            FORM_VALIDATION_TRANSLATIONS.required(field.label)
                          }
                          labelText={field.label}
                          tooltipText={field.tooltipText}
                          required
                        />
                      );
                    })}
                  </div>
                );
              }
            )}
          </div>
        </Form>
      </div>
      <DeleteModal
        modalHeaderText={CREATE_LOCATION_TRANSLATIONS.createModal.header}
        modalDescriptionText={
          CREATE_LOCATION_TRANSLATIONS.createModal.modalContent
        }
        onDelete={() => navigate("/configurationManagement/locations")}
        open={open}
        setOpen={setOpen}
        cancelIcon={DeleteModalIcons.Cancel}
        confirmationButtonLabel={
          CREATE_LOCATION_TRANSLATIONS.buttonLabels.goBack
        }
      />
      <div className="flex justify-end form-footer">
        <div className="mr-2">
          <Button
            className="w-full"
            type="button"
            kind="tertiary"
            disabled={false}
            label={CREATE_LOCATION_TRANSLATIONS.buttonLabels.cancel}
            onClick={() => setOpen(true)}
            dataCy="cancelCreateLocation-"
          />
        </div>

        <div>
          <Button
            className="w-full"
            type="button"
            loading={loading}
            disabled={isFormInvalid({ data: connectionData })}
            onClick={createLocation}
            label={CREATE_LOCATION_TRANSLATIONS.buttonLabels.create}
            dataCy="createLocation-"
          />
        </div>
      </div>
    </>
  );
};

export default NewLocationForm;
